'use client';

import React from 'react';
import AppIcon from '@/core/presentation/components/ui/AppIcon';
import {
  ADMIN_SECTIONS,
  ADMIN_MENU_GROUPS,
} from '../../constants/sections';
import type { AdminSectionId } from '../../constants/sections';

const sectionsById = Object.fromEntries(ADMIN_SECTIONS.map((s) => [s.id, s]));

export interface AdminSidebarProps {
  currentSection: AdminSectionId;
  onSectionChange: (section: AdminSectionId) => void;
  isOpen?: boolean;
  onClose?: () => void;
}

const navItemClass = (active: boolean) =>
  `flex items-center gap-2.5 w-full px-3 py-2.5 rounded-lg text-left text-sm font-medium transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-navy-dark ${
    active
      ? 'bg-cyan/20 text-cyan border border-cyan/30'
      : 'text-white/80 hover:bg-white/10 hover:text-white border border-transparent'
  }`;

export const AdminSidebar: React.FC<AdminSidebarProps> = ({
  currentSection,
  onSectionChange,
  isOpen = true,
  onClose,
}) => {
  return (
    <>
      {onClose && (
        <button
          type="button"
          aria-label="Fechar menu"
          onClick={onClose}
          className={`fixed inset-0 z-40 bg-black/50 lg:hidden transition-opacity duration-200 ${
            isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
          }`}
        />
      )}

      <aside
        className={`
          fixed left-0 z-40 w-64 bg-navy-dark border-r border-white/10
          top-[calc(3.5rem+env(safe-area-inset-top,0px))]
          h-[calc(100dvh-3.5rem-env(safe-area-inset-top,0px))]
          flex flex-col overflow-visible
          transition-transform duration-300 ease-out lg:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        <nav className="flex-1 p-2.5 overflow-y-auto" aria-label="Menu admin">
          {ADMIN_MENU_GROUPS.map((group) => (
            <div key={group.label} className="mb-5 last:mb-0">
              <p className="px-3 py-1 text-[11px] font-semibold text-white/50 uppercase tracking-wider">
                {group.label}
              </p>
              <div className="mt-1 space-y-0.5">
                {group.sectionIds.map((sectionId) => {
                  const item = sectionsById[sectionId];
                  if (!item) return null;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => {
                        onSectionChange(item.id);
                        onClose?.();
                      }}
                      className={navItemClass(currentSection === item.id)}
                    >
                      <AppIcon name={item.iconName} size={18} className="shrink-0" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
};
